import React, { useMemo } from 'react'
import { useSelector, connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { Map, List } from 'immutable'
import { connect as connectStaticText } from '../../StaticText/connect'
import Button, { TYPES as BUTTON_TYPES } from '../../Button/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../../Sherpa/index'
import { getBoundActions } from '../../../actions/index'
import { getUserLanguagePrimary, selectUserRequestCountry } from '../../../services/user/index'
import { historyRedirect } from '../../../services/navigation/index'
import { URL_ACCOUNT_CANCEL_CONFIRM } from '../../../services/url/constants'
import { DE as COUNTRY_DE } from '../../../services/country/index'
import { executeUserAccountCancel } from '../../../services/user-account/index'
import HighlightMessage, { MESSAGE_TYPES } from '../../HighlightMessage/index'
import { renderSymbol, EUR, CAD } from '../../../services/currency/index'
import { FR } from '../../../services/languages/constants'
import { PLAN_SKU_PRICE_INCREASE_2026_MONTHLY, PLAN_SKU_PRICE_INCREASE_2026_ANNUAL, PLAN_CATEGORY_MONTHLY } from '../../../services/plans/index'
import { useGetPlanDataBySku } from '../../../services/plans/hooks'

/**
 * Subscription price
 * @param {Object} props - Component props
 * @returns {JSX.Element} Subscription price
 */
function SubscriptionPrice (props) {
  const { plan, userLanguage, currencyIso } = props
  const cost = plan.get('firstPayment') || plan.getIn(['costs', 0]) || ''
  const currencySymbol = renderSymbol(currencyIso)
  const abbreviation = plan.get('billingPeriodAbbreviation')

  const costStr = String(cost)
  const [amount, cents] = costStr.includes('.') ? costStr.split('.') : costStr.split(',')
  const symbolAfter = currencyIso === EUR || (currencyIso === CAD && userLanguage === FR)
  const decimalSeparator = symbolAfter ? ',' : '.'
  const costClassName = `price__cost${!cents ? ' price__cost--no-cents' : ''}`

  const price = (
    <>
      {!symbolAfter && currencySymbol}
      <span className={costClassName}>
        {amount}
        {cents && <span className="price__cost-cents">{`${decimalSeparator}${cents}`}</span>}
      </span>
      {symbolAfter && currencySymbol}
    </>
  )

  return (
    <><span className="price">{price}</span>/{abbreviation}</>
  )
}

/**
 * Cancel warning modal (price increase)
 * @param {Object} props - Component props
 * @returns {JSX.Element} MyAccountCancelWarningModal
 */
function MyAccountCancelWarningModal (props) {
  const {
    user,
    auth,
    userAccount,
    staticText,
    onCancel,
    dismissModal,
  } = props

  const userLanguage = getUserLanguagePrimary(user)
  const userRequestCountry = useSelector(selectUserRequestCountry)
  const currencyCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
  const isGermanUser = [userRequestCountry, currencyCountry].includes(COUNTRY_DE)
  const authJwt = auth && auth.get ? auth.get('jwt') : null
  const currentPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan']) || Map()
  const planSku = currentPlan.get('sku') || currentPlan.get('planSku')
  const planCategory = currentPlan.get('planCategory')
  const isMonthly = planCategory === PLAN_CATEGORY_MONTHLY
  const newPlanSku = isMonthly
    ? PLAN_SKU_PRICE_INCREASE_2026_MONTHLY : PLAN_SKU_PRICE_INCREASE_2026_ANNUAL
  const infoContent = <>{staticText.getIn(['data', 'info1'])}<span>{staticText.getIn(['data', 'info2'])}</span>{staticText.getIn(['data', 'info3'])}</>

  const planSkus = useMemo(
    () => [
      planSku,
      newPlanSku,
    ],
    [planSku],
  )

  const plansData = useGetPlanDataBySku({
    userLanguage,
    authJwt,
    planSkus,
  }) || Map()

  const plansLoading = plansData.get('loading', false)
  const plansError = plansData.get('error')
  const plansList = plansData.get('plans', List())

  if (plansError) {
    onCancel()
  }

  const newPlanData = plansList.find((plan) => plan.get('sku') === newPlanSku) || Map()
  const planData = plansList.find((plan) => plan.get('sku') === planSku) || Map()
  const currencyIso = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])

  const handleCancel = () => {
    if (isGermanUser) {
      executeUserAccountCancel({
        props,
        historyRedirect,
        userLanguage,
        cancelConfirmUrl: URL_ACCOUNT_CANCEL_CONFIRM,
      })
    } else {
      onCancel()
    }
  }

  return (
    <div className="my-account-cancel-warning-modal">
      <div className={`my-account-cancel-warning-modal__loading ${plansLoading ? '' : 'my-account-cancel-warning-modal__hidden'}`}>
        <Sherpa type={TYPE_SMALL_BLUE} />
      </div>
      <div className={`my-account-cancel-warning-modal__content ${plansLoading ? 'my-account-cancel-warning-modal__hidden' : ''}`}>
        <div className="my-account-cancel-warning-modal__title">{staticText.getIn(['data', 'title'])}</div>
        <div className="my-account-cancel-warning-modal__subtitle">{staticText.getIn(['data', 'subtitle'])}</div>
        <div className="my-account-cancel-warning-modal__content-inner">
          <div className="my-account-cancel-warning-modal__plan">
            <div className="my-account-cancel-warning-modal__plan-copy">{staticText.getIn(['data', 'currentPrice'])}</div>
            <div className="my-account-cancel-warning-modal__plan-cost">
              <SubscriptionPrice
                plan={planData}
                currencyIso={currencyIso}
                userLanguage={userLanguage}
              />
            </div>
          </div>
          <div className="my-account-cancel-warning-modal__plan my-account-cancel-warning-modal__plan--new">
            <div className="my-account-cancel-warning-modal__plan-copy">{staticText.getIn(['data', 'newPrice'])}</div>
            <div className="my-account-cancel-warning-modal__plan-cost">
              <SubscriptionPrice
                plan={newPlanData}
                currencyIso={currencyIso}
                userLanguage={userLanguage}
              />
            </div>
          </div>
        </div>
        <HighlightMessage
          content={infoContent}
          className="my-account-cancel-warning-modal__info"
          type={MESSAGE_TYPES.PINK}
        />
        <div className="my-account-cancel-warning-modal__line"></div>
        <div className="my-account-cancel-warning-modal__buttons">
          <Button
            onClick={dismissModal}
            type={BUTTON_TYPES.PRIMARY}
            className="my-account-cancel-warning-modal__keep-button"
          >
            {staticText.getIn(['data', 'keepButton'])}
          </Button>
          <Button
            onClick={handleCancel}
            type={BUTTON_TYPES.SECONDARY}
            className="my-account-cancel-warning-modal__cancel-button"
          >
            <div>
              {staticText.getIn(['data', 'cancelButton'])}
              <IconV2
                className="my-account-cancel-warning-modal__cancel-button-icon"
                type={ICON_TYPES.CHEVRON_RIGHT}
              />
            </div>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'myAccountCancelWarningModal' }),
  connectRedux(
    (state) => ({
      userAccount: state.userAccount,
      auth: state.auth,
      user: state.user,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        dismissModal: actions.dialog.dismissModal,
        setUserAccountCancelReason: actions.userAccount.setUserAccountCancelReason,
        setUserAccountCancelFormData: actions.userAccount.setUserAccountCancelFormData,
        setUserAccountCancel: actions.userAccount.setUserAccountCancel,
      }
    },
  ),
)(MyAccountCancelWarningModal)
